import * as zod from 'zod';

import {
  IntegrationActionControlBoxIOInterface,
  IntegrationInterfaceKind,
} from '@sb/integrations/io/types';
import { FlangeInputPortID } from '@sb/routine-runner/types/FlangeInputPort';
import { InputPortID } from '@sb/routine-runner/types/InputPort';
import { IOLevel } from '@sb/routine-runner/types/IOLevel';

/**
 * A positioner status line: which port to read, and which level means the
 * condition is met.
 *
 * Shaped like CNCMachine's `IntegrationInputConfig` — a kind-discriminated
 * port plus its asserted level — because a positioner is the same kind of
 * thing: a machine with fixed, named status inputs. The framework's
 * `sensors[]` array is the shape for equipment whose sensors an operator
 * composes, and its generic resolvers read sensors out of *device state*
 * (`ExpressionEvaluator`), which this device does not republish; adopting that
 * shape here bought the indirection without the tooling.
 *
 * Discriminated rather than one flat port enum so a flange input is labeled
 * `flangeIO` and not passed off as a control-box input. Safety ports are
 * absent by construction: a positioner status line is not a safeguard, and
 * offering those would let a rotary weld be gated on an E-Stop input.
 *
 * `activeLevel` is not offered in the equipment form yet, so in practice it
 * holds its `high` default; a low-asserted positioner is a config change
 * rather than a schema one.
 */
export const WeldPositionerSensor = zod.discriminatedUnion('kind', [
  zod.object({
    kind: zod.literal(IntegrationInterfaceKind.Values.controlBoxIO),
    ioPort: InputPortID,
    activeLevel: IOLevel.default('high'),
  }),
  zod.object({
    kind: zod.literal(IntegrationInterfaceKind.Values.flangeIO),
    ioPort: FlangeInputPortID,
    activeLevel: IOLevel.default('high'),
  }),
]);

export type WeldPositionerSensor = zod.infer<typeof WeldPositionerSensor>;

/**
 * The rotation trigger: asserted to turn, released to stop. The positioner
 * halts in place when the signal drops, which is what lets a paused weld
 * resume rather than being scrapped.
 *
 * Modeled as an IO *action* rather than a bare port so it inherits the
 * framework's multi-port handling — a positioner needing two outputs driven
 * together becomes a config change, not a schema change — and so
 * `executeControlBoxIOAction` does the driving.
 *
 * Pulsing is not supported, and the device is what enforces that: it forces
 * `isPulse` off on both assert and release (`WeldPositioner.rotateAction` /
 * `releaseAction`). A pulse would start rotation with nothing holding it on,
 * and releasing the trigger would no longer stop the part. The `isPulse:
 * false` below is only this field's default when `rotate` is absent
 * entirely — `IntegrationActionControlBoxIOOutputs` defaults it to *true* —
 * so the schema alone cannot carry the invariant.
 */
export const WeldPositionerRotateAction =
  IntegrationActionControlBoxIOInterface.default({
    kind: IntegrationInterfaceKind.Values.controlBoxIO,
    outputs: [{ ioPort: null, isHigh: true, isPulse: false }],
    pulseSeconds: 0.5,
  });

export const WeldPositionerConfiguration = zod.object({
  kind: zod.literal('WeldPositioner'),
  name: zod.string().default('Weld positioner'),
  rotate: WeldPositionerRotateAction,
  /**
   * Read before the arc strikes. Null means the cell has no alignment signal,
   * which a step requiring alignment must treat as a configuration error
   * rather than as "aligned". A sensor that is present always names a port —
   * "configured but unreadable" is not a state this can hold.
   */
  alignmentSensor: WeldPositionerSensor.nullable().default(null),
  /** Reports that the part has come back around. Null means time-based only. */
  completionSensor: WeldPositionerSensor.nullable().default(null),
  // No `customIcon`: the positioner shows a fixed lathe icon
  // (`weldPositionerFrontend.iconKind`). `EditEquipment` renders its icon
  // picker for any config carrying that field, and the equipment list prefers
  // a set `customIcon` over the implementation's own, so declaring it is
  // exactly what would let the icon vary.
});

export type WeldPositionerConfiguration = zod.infer<
  typeof WeldPositionerConfiguration
>;
